import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useState } from "react";
import { api, errorMessage } from "../../api/client";
import { usePublicConfig } from "../../api/queries";
import type { MessageResponse } from "../../api/types";
import { useValidatedForm } from "../../lib/useValidatedForm";
import { emailRules, EMAIL_CONSTRAINT, type ValidationConfig } from "../../lib/validation";
import { useFollow } from "../../router/useRoute";
import { PublicShell } from "./PublicShell";
import { ResultPage } from "./ResultPage";
import { useTurnstile } from "./useTurnstile";

const config: ValidationConfig = { email: emailRules };
const FIELD_ORDER = ["email"] as const;

interface Props {
  mode: "resend-verification" | "forgot-password";
}

/** Hai luồng chỉ khác endpoint và câu chữ, nên dùng chung một component. */
export function EmailActionPage({ mode }: Props) {
  const resend = mode === "resend-verification";
  const follow = useFollow();
  const { data: publicConfig } = usePublicConfig();
  const turnstile = useTurnstile(publicConfig);
  const form = useValidatedForm<"email">(config, { email: "" }, FIELD_ORDER);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError("");
    if (!form.submit()) return;
    if (turnstile.enabled && !turnstile.token) {
      setFormError("Hoàn tất bước xác minh chống bot rồi thử lại.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api<MessageResponse>(`/auth/${mode}`, {
        method: "POST",
        body: { email: form.values.email.trim(), turnstile_token: turnstile.token },
      });
      setDone(result.message);
    } catch (error) {
      setFormError(errorMessage(error));
      turnstile.reset();
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <ResultPage title="Kiểm tra email" detail={done} actionLabel="Về trang đăng nhập" actionHref="/login" />;
  }

  return (
    <PublicShell
      title={resend ? "Gửi lại email xác minh" : "Quên mật khẩu"}
      description={
        resend
          ? "Nhập email đang chờ kích hoạt để nhận liên kết xác minh mới."
          : "Nhập email tài khoản để nhận liên kết đặt lại mật khẩu."
      }
      footer={
        <Box textAlign="center">
          <Link href="/login" onFollow={follow}>
            Quay lại đăng nhập
          </Link>
        </Box>
      }
    >
      <form onSubmit={handleSubmit}>
        <Form
          errorText={formError || undefined}
          errorIconAriaLabel="Lỗi"
          actions={
            <Button variant="primary" loading={submitting} loadingText="Đang gửi">
              {resend ? "Gửi lại email" : "Gửi hướng dẫn"}
            </Button>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Email FaceGo" constraintText={EMAIL_CONSTRAINT} errorText={form.errors.email}>
              <Input
                ref={form.refFor("email")}
                type="email"
                autoComplete="email"
                placeholder="ban@example.com"
                value={form.values.email}
                onChange={({ detail }) => form.setValue("email", detail.value)}
                onBlur={() => form.handleBlur("email")}
              />
            </FormField>
            {turnstile.enabled && (
              <FormField label="Xác minh chống bot">
                <div ref={turnstile.containerRef} />
              </FormField>
            )}
          </SpaceBetween>
        </Form>
      </form>
    </PublicShell>
  );
}
